import { Plus, X } from "lucide-react";
import { type FocusEvent, useId, useRef, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { LoadingRows } from "@/components/shared/loading-rows";
import { ReorderButtons } from "@/components/shared/reorder-buttons";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  type EmailTemplateRow,
  useSaveTemplate,
  useTemplatePreview,
} from "@/hooks/use-email-templates";
import { sameTemplateContent, type TemplateContent } from "@/lib/email-template-content";

interface NotificationTemplateEditorProps {
  template: EmailTemplateRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The field a variable chip inserts into: `subject`, `heading`, `preheader`, `cta` or `p<index>`. */
type FieldTarget = { id: string; el: HTMLInputElement | HTMLTextAreaElement };

function swap<T>(list: T[], a: number, b: number): T[] {
  const next = [...list];
  const x = next[a];
  const y = next[b];
  if (x === undefined || y === undefined) return list;
  next[a] = y;
  next[b] = x;
  return next;
}

/**
 * Rewording one review email. The parent mounts this only while open (keyed
 * per template), so the form always starts from the saved wording.
 */
export function NotificationTemplateEditor({
  template,
  open,
  onOpenChange,
}: NotificationTemplateEditorProps) {
  const [content, setContent] = useState<TemplateContent>(() => template.content);
  const lastFocused = useRef<FieldTarget | null>(null);
  // React keys for the paragraph rows, moved in step with the paragraphs so a
  // reorder moves the textarea (and its focus) rather than rewriting values.
  const keySeq = useRef(0);
  const newKey = () => {
    keySeq.current += 1;
    return `para-${keySeq.current}`;
  };
  const [rowKeys, setRowKeys] = useState<string[]>(() =>
    template.content.bodyParagraphs.map(() => newKey()),
  );
  const save = useSaveTemplate(template.key);
  const debounced = useDebouncedValue(content, 400);
  const preview = useTemplatePreview(template.key, debounced, open);
  const ids = { subject: useId(), heading: useId(), preheader: useId(), cta: useId() };

  const dirty = !sameTemplateContent(content, template.content);
  const isDefault = sameTemplateContent(content, template.defaultContent);
  const paragraphs = content.bodyParagraphs;
  const last = paragraphs.length - 1;

  function readField(id: string): string {
    if (id === "subject") return content.subject;
    if (id === "heading") return content.heading;
    if (id === "preheader") return content.preheader;
    if (id === "cta") return content.ctaLabel ?? "";
    return paragraphs[Number(id.slice(1))] ?? "";
  }

  function setField(id: string, value: string) {
    setContent((c) => {
      if (id === "subject") return { ...c, subject: value };
      if (id === "heading") return { ...c, heading: value };
      if (id === "preheader") return { ...c, preheader: value };
      // An empty button label means "no button text"; the server stores null.
      if (id === "cta") return { ...c, ctaLabel: value === "" ? null : value };
      const index = Number(id.slice(1));
      return { ...c, bodyParagraphs: c.bodyParagraphs.map((p, i) => (i === index ? value : p)) };
    });
  }

  function track(id: string) {
    return (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      lastFocused.current = { id, el: e.currentTarget };
    };
  }

  function insertVariable(name: string) {
    const token = `{{${name}}}`;
    const target = lastFocused.current;
    // A field that has since been removed (a deleted paragraph) is no target.
    if (!target?.el.isConnected) {
      setField("subject", content.subject + token);
      return;
    }
    // Read the field id off the element, not the stored one: a paragraph that
    // has been moved since it was focused now sits at a different index.
    const id = target.el.dataset.field ?? target.id;
    const value = readField(id);
    const start = target.el.selectionStart ?? value.length;
    const end = target.el.selectionEnd ?? start;
    setField(id, value.slice(0, start) + token + value.slice(end));
    const el = target.el;
    const caret = start + token.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }

  function moveParagraph(index: number, direction: "up" | "down") {
    const other = direction === "up" ? index - 1 : index + 1;
    setContent((c) => ({ ...c, bodyParagraphs: swap(c.bodyParagraphs, index, other) }));
    setRowKeys((keys) => swap(keys, index, other));
  }

  function removeParagraph(index: number) {
    setContent((c) => ({ ...c, bodyParagraphs: c.bodyParagraphs.filter((_, i) => i !== index) }));
    setRowKeys((keys) => keys.filter((_, i) => i !== index));
  }

  function addParagraph() {
    setContent((c) => ({ ...c, bodyParagraphs: [...c.bodyParagraphs, ""] }));
    setRowKeys((keys) => [...keys, newKey()]);
  }

  function resetToDefault() {
    setContent(template.defaultContent);
    setRowKeys(template.defaultContent.bodyParagraphs.map(() => newKey()));
  }

  async function onSave() {
    // The hook toasts the error itself; this only closes the dialog on success.
    await save
      .mutateAsync(content)
      .then(() => onOpenChange(false))
      .catch(() => undefined);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Edit “{template.label}”</DialogTitle>
          <DialogDescription>
            {template.description ?? "Change the wording of this email."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={ids.subject}>Subject</Label>
              <Input
                id={ids.subject}
                data-field="subject"
                value={content.subject}
                aria-invalid={content.subject.trim() === ""}
                onFocus={track("subject")}
                onChange={(e) => setField("subject", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={ids.heading}>Heading</Label>
              <Input
                id={ids.heading}
                data-field="heading"
                value={content.heading}
                onFocus={track("heading")}
                onChange={(e) => setField("heading", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={ids.preheader}>Preheader</Label>
              <Input
                id={ids.preheader}
                data-field="preheader"
                value={content.preheader}
                onFocus={track("preheader")}
                onChange={(e) => setField("preheader", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <p className="font-medium text-sm">Body</p>
              {paragraphs.map((paragraph, i) => (
                <div key={rowKeys[i] ?? `extra-${paragraph}`} className="flex items-start gap-2">
                  <Textarea
                    data-field={`p${i}`}
                    aria-label={`Paragraph ${i + 1}`}
                    value={paragraph}
                    className="min-h-[72px] flex-1"
                    onFocus={track(`p${i}`)}
                    onChange={(e) => setField(`p${i}`, e.target.value)}
                  />
                  <div className="flex flex-col items-center gap-1">
                    <ReorderButtons
                      label={`Paragraph ${i + 1}`}
                      onMove={(direction) => moveParagraph(i, direction)}
                      disableUp={i === 0}
                      disableDown={i === last}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={`Remove paragraph ${i + 1}`}
                      onClick={() => removeParagraph(i)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addParagraph}>
                <Plus className="mr-1 h-4 w-4" />
                Add paragraph
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor={ids.cta}>Button label</Label>
              <Input
                id={ids.cta}
                data-field="cta"
                value={content.ctaLabel ?? ""}
                onFocus={track("cta")}
                onChange={(e) => setField("cta", e.target.value)}
              />
              <p className="text-muted-foreground text-xs">The link itself can't be edited.</p>
            </div>

            {template.variables && template.variables.length > 0 && (
              <div className="space-y-2">
                <p className="font-medium text-sm">Variables</p>
                <div className="flex flex-wrap gap-2">
                  {template.variables.map((variable) => (
                    <Button
                      key={variable.name}
                      variant="outline"
                      size="sm"
                      title={variable.description}
                      // Keep focus (and the caret) in the field being edited.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => insertVariable(variable.name)}
                    >
                      {`{{${variable.name}}}`}
                    </Button>
                  ))}
                </div>
                <p className="text-muted-foreground text-xs">
                  Click a field, then a variable to insert it.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <p className="font-medium text-sm">Preview</p>
            {preview.isError ? (
              <ErrorState
                message={preview.error?.message ?? "Preview failed"}
                onRetry={() => preview.refetch()}
              />
            ) : !preview.data ? (
              <LoadingRows />
            ) : (
              <>
                <p className="font-semibold text-sm">{preview.data.subject}</p>
                {/* Rendered email HTML is isolated: no scripts, no same-origin. */}
                <iframe
                  title="Email preview"
                  sandbox=""
                  srcDoc={preview.data.html}
                  className="h-[28rem] w-full rounded border bg-white"
                />
              </>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" disabled={isDefault || save.isPending} onClick={resetToDefault}>
            Reset to default
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" disabled={save.isPending} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={!dirty || content.subject.trim() === "" || save.isPending}
              onClick={onSave}
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
